import { ANNOTATION_SOURCE, BBOX_TRACKING_ERROR_MESSAGES } from '../constants';
import type { OutputAnnotationT, OutputLabelledDataT } from '../image';
import { BBOX_TRACKING_NUM_FRAMES_TO_TRACK } from '../image/labelling/constants';
import type { BoundingBoxTrackingInputT, BoundingBoxTrackingOutputT, SamModelImagePluginRequestT, SamModelVideoPluginRequestT, SAMSegmentImageData, SAMSegmentVideoData, TrackObjectsInputT, TrackObjectsOutputT } from '../image/types';
import { BASE_API_URL } from './consts/base-url';
import type { OutputData } from './types/task';

// copied from src/platform/gss/common/@uber/labeling-common/src/utils/segmentation.ts
export const getCanvasCtx = async (imageUrl?: string) => {
  if (!imageUrl) return null;
  const canvas = document.createElement('canvas');
  const image = new Image();
  try {
    image.src = imageUrl;
    await image.decode();
  } catch (e) {
    if (typeof e === 'string') {
      console.error(e);
    }
    if (e instanceof Error) {
      console.error(e.message);
    }
    return null;
  }
  canvas.width = image.width;
  canvas.height = image.height;
  const canvasCtx = canvas.getContext('2d');
  return canvasCtx;
};
export const transformSegmentationImageToPoints = async (data: {
  imgData?: string;
  color: string;
}, defaultCanvasCtx?: CanvasRenderingContext2D) => {
  const {
    imgData,
    color
  } = data;
  let canvasCtx: CanvasRenderingContext2D | undefined | null = defaultCanvasCtx;
  if (!defaultCanvasCtx) {
    canvasCtx = await getCanvasCtx(imgData);
  }
  if (!canvasCtx) return null;
  const image = new Image();
  try {
    image.src = imgData ?? '';
    await image.decode();
  } catch (e) {
    console?.error(String(e));
    return null;
  }
  canvasCtx.drawImage(image, 0, 0);
  const imgDataArray = canvasCtx.getImageData(0, 0, canvasCtx.canvas.width, canvasCtx.canvas.height).data;
  const points: [number, number][] = [];
  const NO_OF_ITEMS_PER_PIXEL = 4;
  for (let i = 0; i < imgDataArray.length + NO_OF_ITEMS_PER_PIXEL; i += NO_OF_ITEMS_PER_PIXEL) {
    const x = i / NO_OF_ITEMS_PER_PIXEL % canvasCtx.canvas.width;
    const y = Math.floor(i / NO_OF_ITEMS_PER_PIXEL / canvasCtx.canvas.width);
    // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
    if (imgDataArray[i] > 0 || imgDataArray[i + 1] > 0 || imgDataArray[i + 2] > 0) {
      points.push([x, y]);
    }
  }
  return {
    color,
    points
  };
};

/**
 * Removes non annotated black regions from the mask image received from server
 */
export const removeNonAnnotatedNonWhiteRegions = async (imgData?: string) => {
  if (!imgData) {
    console.error('No image data provided');
    return null;
  }
  const canvasCtx = await getCanvasCtx(imgData);
  if (!canvasCtx) {
    console.error('No canvas context found');
    return null;
  }
  const image = new Image();
  try {
    image.src = imgData;
    await image.decode();
  } catch (e) {
    console.error(String(e));
    return null;
  }
  canvasCtx.drawImage(image, 0, 0);
  const imageData = canvasCtx.getImageData(0, 0, canvasCtx.canvas.width, canvasCtx.canvas.height);
  const data = imageData.data;
  for (let i = 0; i < data.length; i += 4) {
    // Check if pixel is not white (R=255, G=255, B=255) and has some opacity (A>0)
    // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
    if (data[i + 3] > 0 && !(data[i] === 255 && data[i + 1] === 255 && data[i + 2] === 255)) {
      // Set alpha channel to 0
      data[i + 3] = 0;
    }
  }
  canvasCtx.putImageData(imageData, 0, 0);
  return canvasCtx.canvas.toDataURL('image/png');
};
export const getSamImageSegmentData = async (input: SamModelImagePluginRequestT): Promise<SAMSegmentImageData | null | Error> => {
  try {
    const response = await fetch(`${BASE_API_URL}/segment/image`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        imageUrl: input.imageUrl,
        segmentationType: input.imageSegmentationType,
        points: input.points,
        box: input.box
      })
    });
    const predictedMasks = (await response.json()).predictions;
    if (predictedMasks[0].points) {
      return {
        points: predictedMasks[0].points.map(point => [point.x, point.y])
      };
    }
    const mask = predictedMasks[0].mask;
    const transformedMask = mask ? await removeNonAnnotatedNonWhiteRegions(`data:image/png;base64,${mask}`) : '';
    return transformedMask ? {
      mask: transformedMask
    } : null;
  } catch (error) {
    console.error(`${error}`);
    return new Error(`Failed to fetch SAM segment data`);
  }
};
export const getBoundingBoxTrackingData = async (input: BoundingBoxTrackingInputT): Promise<BoundingBoxTrackingOutputT | Error> => {
  try {
    const response = await fetch(`${BASE_API_URL}/video/track`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        videoUrl: input.videoUrl,
        initialFrame: input.initialFrame + 1,
        bbox: input.bbox,
        numFramesToTrack: input?.numFramesToTrack ?? BBOX_TRACKING_NUM_FRAMES_TO_TRACK
      })
    });
    if (response.status !== 200) {
      return new Error(BBOX_TRACKING_ERROR_MESSAGES.BBOX_TRACKING_FAILED);
    }
    const {
      bboxList
    } = await response.json();
    return {
      bboxList
    };
  } catch (error) {
    console.error(`${error}`);
    return new Error(BBOX_TRACKING_ERROR_MESSAGES.BBOX_TRACKING_FAILED);
  }
};

// Batch keyframe tracking: sends all of one object's adjusted keyframes in a single call
// (BE resolves the media from `taskId`) and returns one tracked object across the range.
// Mirrors getBoundingBoxTrackingData's fetch/error convention.
export const trackObjectsAcrossKeyframes = async (input: TrackObjectsInputT): Promise<TrackObjectsOutputT | Error> => {
  try {
    const response = await fetch(`${BASE_API_URL}/video/sam-2/track-objects`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        taskId: input.taskId,
        keyFrames: input.keyFrames,
        startFrame: input.startFrame,
        endFrame: input.endFrame
      })
    });
    if (response.status !== 200) {
      return new Error(BBOX_TRACKING_ERROR_MESSAGES.BBOX_TRACKING_FAILED);
    }
    return (await response.json() as TrackObjectsOutputT);
  } catch (error) {
    console.error(`${error}`);
    return new Error(BBOX_TRACKING_ERROR_MESSAGES.BBOX_TRACKING_FAILED);
  }
};
export const getSamVideoSegmentDataV2 = async (input: SamModelVideoPluginRequestT): Promise<SAMSegmentVideoData | null | Error> => {
  try {
    const response = await fetch(`${BASE_API_URL}/segment/video`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        videoUrl: input.videoUrl,
        videoSegmentationType: input.videoSegmentationType,
        videoPoints: input.points,
        videoBox: input.box,
        initialFrame: input.frameIndex,
        startFrame: input.frameRange.startFrame,
        endFrame: input.frameRange.endFrame + 1
      })
    });
    if (response.status !== 200) {
      return new Error(`Failed to fetch SAM segment data`);
    }
    const {
      predictions: videoPredictions
    } = await response.json();
    return processVideoPredictionsToFrameData(videoPredictions);
  } catch (error) {
    console.error(`${error}`);
    return new Error(`Failed to fetch SAM segment data`);
  }
};
const processVideoPredictionsToFrameData = videoPredictions => {
  return videoPredictions.map(prediction => {
    if (prediction.prediction?.points) {
      return {
        points: prediction.prediction?.points.map(point => ([point.x, point.y] as [number, number]))
      };
    } else if (prediction.prediction?.mask) {
      const mask = prediction.prediction.mask ? 'data:image/png;base64,' + prediction.prediction.mask : '';
      return {
        mask
      };
    }
    return {
      mask: ''
    };
  });
};
export const isImageTask = (input?: OutputData) => input?.labellingType === 'IMAGE';
export const isVideoTask = (input?: OutputData) => input?.labellingType === 'VIDEO';
export const isYoloAnnotation = (annotation: OutputAnnotationT) => annotation?.metadata?.annotationSource === ANNOTATION_SOURCE.YOLO;
export const isUserCreatedAnnotation = (annotation: OutputAnnotationT) => !annotation?.metadata?.annotationSource;
export const filterAnnotations = (annotations: OutputAnnotationT[] = [], filterFn: (annotation: OutputAnnotationT) => boolean) => annotations.filter(filterFn);
export const getYoloGeneratedData = (input?: OutputData) => {
  if (input && isImageTask(input)) {
    return [filterAnnotations(input?.annotations, isYoloAnnotation)];
  } else if (input && isVideoTask(input)) {
    return input?.frames.map(frame => filterAnnotations(frame.annotations, isYoloAnnotation));
  }
  return [];
};
export const getFilteredInput = (input?: OutputData): OutputLabelledDataT | undefined => {
  if (input && isImageTask(input)) {
    return {
      ...input,
      annotations: filterAnnotations(input?.annotations, isUserCreatedAnnotation)
    };
  } else if (input && isVideoTask(input)) {
    return {
      ...input,
      frames: input?.frames?.map(frame => ({
        ...frame,
        annotations: filterAnnotations(frame.annotations, isUserCreatedAnnotation)
      }))
    };
  }
  return input;
};